/* eslint-disable tree-shaking/no-side-effects-in-initialization */
import { z } from "zod";
import { zReadonlyArray } from "../zod/utils.js";

import { notNullish } from "../filterGuards.js";
import { VariableName } from "../typeBrands.js";

import {
  CellReferencesParseError,
  CellReferencesV1,
  FunctionDefinition,
  ParamReference,
  SourceLocation,
  mergeParamReferences,
} from "./cellReferencesV1.js";

export function createEmptyParamReferences(
  params: readonly VariableName[],
): ParamReference[] {
  return params.map((param) => ({
    param,
    locations: [],
  }));
}

export function upgradeCellReferencesV1<T extends CellReferencesV1>(
  references: T,
): Omit<T, keyof CellReferencesV1> & CellReferencesV2 {
  return {
    ...references,
    newParams: createEmptyParamReferences(references.newParams),
    referencedParams: createEmptyParamReferences(references.referencedParams),
  };
}

export function downgradeCellReferencesV2<T extends CellReferencesV2>(
  references: T,
): Omit<T, keyof CellReferencesV2> & CellReferencesV1 {
  return {
    ...references,
    newParams: references.newParams.map((ref) => ref.param),
    referencedParams: references.referencedParams.map((ref) => ref.param),
  };
}

export function mergeCellReferencesV2(
  ...refs: Array<CellReferencesV2 | null | undefined>
): CellReferencesV2 {
  const nonNullRefs = refs.filter(notNullish);
  return {
    newParams: mergeParamReferences(
      ...nonNullRefs.map(({ newParams }) => newParams),
    ),
    referencedParams: mergeParamReferences(
      ...nonNullRefs.map(({ referencedParams }) => referencedParams),
    ),
  };
}

export const ErrorDefinition = z.object({
  errorType: z.string(),
  description: z.string(),
  location: SourceLocation,
});
export type ErrorDefinition = z.infer<typeof ErrorDefinition>;

export const CellReferencesV2 = z.object({
  referencedParams: zReadonlyArray(ParamReference),
  newParams: zReadonlyArray(ParamReference),
});

export type CellReferencesV2 = z.infer<typeof CellReferencesV2>;

export const PythonCellReferencesV2 = CellReferencesV2.extend({
  imports: zReadonlyArray(VariableName),
  importStrings: zReadonlyArray(z.string()),
  functions: zReadonlyArray(FunctionDefinition),
  errors: zReadonlyArray(ErrorDefinition).optional(),
});
export type PythonCellReferencesV2 = z.infer<typeof PythonCellReferencesV2>;

export const RCellReferences = PythonCellReferencesV2;
export type RCellReferences = z.infer<typeof RCellReferences>;

export const SqlCellReferencesV2 = CellReferencesV2.extend({
  containsBlocks: z.boolean(),
});
export type SqlCellReferencesV2 = z.infer<typeof SqlCellReferencesV2>;

export const ParsedPythonCellReferencesV2 = z.union([
  PythonCellReferencesV2,
  CellReferencesParseError,
]);
export type ParsedPythonCellReferencesV2 = z.infer<
  typeof ParsedPythonCellReferencesV2
>;

export const ParsedRCellReferences = z.union([
  RCellReferences,
  CellReferencesParseError,
]);
export type ParsedRCellReferences = z.infer<typeof ParsedRCellReferences>;
